import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import course05 from "../../../course/course-05/course";
import lesson0117 from "../../course-01/lesson-17/lesson";
import lesson0503 from "../../course-05/lesson-03/lesson";
const linkLesson0117 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0117.id);
const linkLesson0503 = getLinkForCourseLesson(course05.id, lesson0503.id);

const theory = `<p>Binaural hearing (i.e. hearing with two ears) provides a range of cues which our brains can process in order to determine the position of sound sources in three-dimensional space [1]. Localization cues can be categorized into two main types: those based on the differences between the signals arriving at the left and right ears, which are responsible mainly for lateralization (i.e. left-right discrimination), and those resulting from the interaction of incoming sound waves with the anatomical features of the listener, which enable up-down as well as front-back discrimination [7, 3, 4] .
<p>
   Under natural listening conditions, both types of localization cues are usually accompanied by head or sound source movements, which aid in the resolution of localization ambiguities and are often denoted as <span class=cmti-10>motion </span>or <span class=cmti-10>dynamic cues</span>. in addition to this, the perceived sound source level (related to SPL), is the main indicator for the distance (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>
   ) of a sound source.<br class=newline>
<p>
   In terms of the Minimum Audible Angle (MAA), a measure of spatial resolution defined as the minimum angular separation required between two sound sources for them to be correctly detected 75% of the time, humans are able to distinguish between broad-band sources separated by 1<span class=tcrm-1000>°</span> in the horizontal plane and 2<span class=tcrm-1000>°</span>-4<span class=tcrm-1000>°</span> in the median plane [5]. <a href=# data-img-alt="Figure 1. Localization blur in the horizontal plane (a) and in the median plane (b)." data-img-src=${config.ASSETS_URL}/02/12/theory/figure-1.svg>Figure 1</a> shows some of the regions in the horizontal and median plane where localization blur occurs. As depicted in the figure, the blur is worse in the median plane than the horizontal plane. Within the median plane, this blur increases with elevation. The values cited are from an experiment which used speech as a stimuli.
   <figure class=figure>
<p><img alt="Figure 1. Localization blur in the horizontal plane (a) and in the median plane (b)." src=${config.ASSETS_URL}/02/12/theory/figure-1.svg><figcaption class=caption>Figure 1. Localization blur in the horizontal plane (a) and in the median plane (b).</figcaption></figure>
<h4 class=likesubsectionHead>Interaural time difference</h4>
<p>
   As soon as a sound source is located outside the median plane (i.e. when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>φ</mi>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>0</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   or
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle class=text>
         <mtext>180</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   ), sound waves radiating from it will travel along paths of different lengths before reaching the left and the right ears, this is illustrated in <a href=# data-img-alt="Figure 2. Sound waves traveling from one sound source reaching two ears by following two different paths." data-img-src=${config.ASSETS_URL}/02/12/theory/figure-2.svg>Figure 2</a>. These different path lengths will in turn produce a delay between the times of arrival of sound at both ears, commonly referred to as Interaural Time Difference (ITD).
   <figure class=figure>
<p><img alt="Figure 2. Sound waves traveling from one sound source reaching two ears by following two different paths." src=${config.ASSETS_URL}/02/12/theory/figure-2.svg><figcaption class=caption>Figure 2. Sound waves traveling from one sound source reaching two ears by following two different paths.</figcaption></figure><figure class=figure><p><img alt="Figure 3. ITD vs azimuth angle (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>φ</mi></math>) in the horizontal plane (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>𝜃</mi><mo class=MathClass-rel>=</mo><mn>0</mn><mstyle class=text><mtext>°</mtext></mstyle></math>)." src=${config.ASSETS_URL}/02/12/theory/figure-3.svg><figcaption class=caption>Figure 3. ITD vs azimuth angle (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>φ</mi></math>) in the horizontal plane (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>𝜃</mi><mo class=MathClass-rel>=</mo><mn>0</mn><mstyle class=text><mtext>°</mtext></mstyle></math>).</figcaption></figure>
<p>
   Even though these delays are very small (generally below
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle class=text>
         <mtext>1</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>ms</mtext>
      </mstyle>
   </math>
   ) and are not perceived consciously, our brains are capable of deriving directional information from them. The ITD typically reaches its maximum when a sound source is located exactly to the left (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>φ</mi>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>270</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   ) or to the right (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>φ</mi>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>90</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   ) of the listener. To illustrate this, <a href=# data-img-alt="Figure 3. ITD vs azimuth angle (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>φ</mi></math>) in the horizontal plane (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>𝜃</mi><mo class=MathClass-rel>=</mo><mn>0</mn><mstyle class=text><mtext>°</mtext></mstyle></math>)." data-img-src=${config.ASSETS_URL}/02/12/theory/figure-3.svg>Figure 3</a> shows ITD values as a function of the azimuth angle (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>φ</mi>
   </math>
   ) in the horizontal plane (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>0</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   ).
<p>
   Sound source localization based on ITDs is especially relevant for lower frequency components (below 1.5 kHz), where the different path lengths and associated timing differences produce easily detectable phase differences between the sound waves reaching both ears, known as Interaural Phase Differences (IPDs). Towards higher frequencies ITDs can be estimated from the envelope of the ear signals (i.e. the group delay
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>τ</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
   </math>
   ), nonetheless, their dominance in sound source localization is reduced with increasing frequency [8, 4].
<h4 class=likesubsectionHead>Interaural level difference</h4>
<p>
   In addition to ITDs, the natural attenuation of sound with increasing distance from the source and the head shadow effect will produce a difference in Sound Pressure Level (SPL) between both ears. This is denoted as Interaural Level Difference (ILD). A sound source located closer to the right ear will therefore generate a higher SPL at the same-sided (ipsilateral) ear than at the opposite-sided (contralateral) ear. This occurs in part because of the different path lengths that sound waves travel, but mainly because the listener’s anatomy affects the propagation of sound waves at different frequencies through diffraction (refer to <a href="${linkLesson0117}" target="_blank">Lesson 1.17</a> for more information), reflection, and absorption (refer to <a href="${linkLesson0503}" target="_blank">Lesson 5.3</a> for more information).
   <figure class=figure>
<p><img alt="Figure 4. ILD vs angle of incidence." src=${config.ASSETS_URL}/02/12/theory/figure-4.svg><figcaption class=caption>Figure 4. ILD vs angle of incidence.</figcaption></figure>
<p>
   Sound components with shorter wavelengths (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
   </math>
   ) are affected to a greater extent by head shadowing and other interactions with the listeners anatomy, resulting in higher ILD values at higher frequencies [4]. These frequency and angle dependencies of the ILD are illustrated in <a href=# data-img-alt="Figure 4. ILD vs angle of incidence." data-img-src=${config.ASSETS_URL}/02/12/theory/figure-4.svg>Figure 4</a>. For wavelengths greater than a typical head dimension (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
      <mo class=MathClass-rel>≫</mo>
      <mstyle class=text>
         <mtext>0.2</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>m</mtext>
      </mstyle>
   </math>
   ) the effects are mostly negligible and only become more pronounced at higher frequencies. Similar to ITDs, ILDs have a minimum when the source is located in the median plane (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>φ</mi>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>0</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   or
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle class=text>
         <mtext>180</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   ) and reach a maximum when the source is located at either side of the listener (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>φ</mi>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>90</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   or
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle class=text>
         <mtext>270</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   ).
<h4 class=likesubsectionHead>Spectral cues</h4>
<figure class=figure>
   <p>
      <img alt="Figure 5. Probability (in %) of direction detection associated with different frequency regions [2]." src=${config.ASSETS_URL}/02/12/theory/figure-5.svg>
      <figcaption class=caption>Figure 5. Probability (in %) of direction detection associated with different frequency regions [2].</figcaption>
</figure>
<p>
   Due to the left-right symmetry of the human head, sources located somewhere along the median plane (i.e.
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>φ</mi>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>0</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   or
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle class=text>
         <mtext>180</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   ), do not produce any interaural differences. In such situations, localization can only be achieved through the identification of direction-dependent filtering patterns of sound caused by the anatomic shape of the auricles, the head, pinna, the shoulders and the upper body. Depending on the direction of sound incidence, the frequency spectrum of the signal reaching the ear is distorted in a distinctive way. Elevation is perceived because of peaks or notches which are caused by pinna resonance and anti-resonance respectively [6].
<p>These specific spectral patterns can be used for localization by the brain, which associates distortions in certain frequency bands with different directions of sound incidence [2], as visualized in <a href=# data-img-alt="Figure 5. Probability (in %) of direction detection associated with different frequency regions [2]." data-img-src=${config.ASSETS_URL}/02/12/theory/figure-5.svg>Figure 5</a>. From the figure we can see that regardless of the sound source location, tones and narrow band noises tend to be localized based on their frequencies within the median plane. For example, a narrow band sound with centre frequency at 1 kHz has a high probability of being localized at the back, regardless of the actual sound source location. Blauert hypothesized that this is the result of relative boosting of certain frequency bands caused by the pinna [2]. However, this behaviour is limited to narrow-band sounds, such as those used in the corresponding experiment, and cannot be extrapolated to the localization of broadband signals.
<h4 class=likesubsectionHead>Motion cues</h4>
<p>
   The previously mentioned localization cues, which are present in a static source-receiver (i.e. listener) configuration, might convey ambiguous information for certain sound source positions. For example, a source located on the front right at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>φ</mi>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>45</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   can produce very similar ITD and ILD values as a source located on the back right at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>φ</mi>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>135</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>°</mtext>
      </mstyle>
   </math>
   , making front-back discrimination particularly difficult. Such areas producing similar localization cues and potential localization errors are arranged as cones around both ears of the listener and are therefore known as <span class=cmti-10>cones of confusion</span> [4]. In the presence of such cone-of-confusion ambiguities, natural source or listener movements (e.g. head rotations) can supply additional localization information in the form of (small) modifications of the ILDs and ITDs, leading to the resolution of localization conflicts. This additional information obtained from the movement of the listener or the sound source can be categorized as motion-based or dynamic localization cues.
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>ILD
  <dd>Interaural Level Difference
  <dt>IPD
  <dd>Iteraural Phase Difference
  <dt>ITD
  <dd>Interaural Time Difference
  <dt>MAA
  <dd>Minimum Audible Angle
  <dt>SPL
  <dd>Sound Pressure Level
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-avan2015>[1]
   <dd class=thebibliography id=bib-1>
      <p>Paul Avan, Fabrice Giraudet, and Béla Büki. “Importance of Binaural Hearing”. In: <span class=cmti-10>Audiology and Neurotology </span>20.1 (May 2015), pp. 3–6. <span class=cmcsc-10><span class=small-caps>issn</span></span>: 1420-3030, 1421-9700. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a class=url href=https://doi.org/10.1159/000380741 target="_blank">10.1159/00038074</a>.
   <dt class=thebibliography id=X0-blauert1969>[2]
   <dd class=thebibliography id=bib-2>
      <p>Jens Blauert. “Sound Localization in the Median Plane”. In: <span class=cmti-10>Acta Acustica </span><span class=cmti-10>united with Acustica </span>22 (Nov. 1969).
   <dt class=thebibliography id=X0-musicant1985>[3]
   <dd class=thebibliography id=bib-3>
      <p>Alan D. Musicant and Robert A. Butler. “Influence of Monaural Spectral Cues on Binaural Localization”. In: <span class=cmti-10>The Journal of the Acoustical </span><span class=cmti-10>Society of America </span>77.1 (Jan. 1985), pp. 202–208. <span class=cmcsc-10><span class=small-caps>issn</span></span>: 0001-4966. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a class=url href=https://doi.org/10.1121/1.392259 target="_blank">10.1121/1.392259</a>.
   <dt class=thebibliography id=X0-risoud2018>[4]
   <dd class=thebibliography id=bib-4>
      <p>M. Risoud et al. “Sound Source Localization”. In: <span class=cmti-10>European Annals </span><span class=cmti-10>of Otorhinolaryngology, Head and Neck Diseases </span>135.4 (Aug. 2018), pp. 259–264. <span class=cmcsc-10><span class=small-caps>issn</span></span>: 18797296. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a class=url href=https://doi.org/10.1016/j.anorl.2018.04.009 target="_blank">10.1016/j.anorl.2018.04.009</a>.
   <dt class=thebibliography id=X0-stecker2012>[5]
   <dd class=thebibliography id=bib-5>
      <p>G Christopher Stecker and Frederick Gallun. “Binaural Hearing, Sound Localization, and Spatial Hearing”. In: Jan. 2012. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-1-59756-202-7.
   <dt class=thebibliography id=X0-takemoto2012>[6]
   <dd class=thebibliography id=bib-6>
      <p>Hironori Takemoto et al. “Mechanism for Generating Peaks and Notches of Head-Related Transfer Functions in the Median Plane”. In: <span class=cmti-10>The Journal of </span><span class=cmti-10>the Acoustical Society of America </span>132.6 (Dec. 2012), pp. 3832–3841. <span class=cmcsc-10><span class=small-caps>issn</span></span>: 0001-4966. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a class=url href=https://doi.org/10/f4fzbx target="_blank">10/f4fzbx</a>.
   <dt class=thebibliography id=X0-wright2001>[7]
   <dd class=thebibliography id=bib-7>
      <p>Beverly A. Wright and Matthew B. Fitzgerald. “Different Patterns of Human Discrimination Learning for Two Interaural Cues to Sound-Source Location”. In: <span class=cmti-10>Proceedings of the National Academy of Sciences </span>98.21 (Oct. 9, 2001), pp. 12307–12312. <span class=cmcsc-10><span class=small-caps>issn</span></span>: 0027-8424, 1091-6490. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a class=url href=https://doi.org/10.1073/pnas.211220498 target="_blank">10.1073/pnas.211220498</a>.
   <dt class=thebibliography id=X0-xie2013>[8]
   <dd class=thebibliography id=bib-8>
      <p>Bosun Xie. <span class=cmti-10>Head-Related Transfer Function and Virtual Auditory Display</span>. 2. ed. Plantation, Fla: J. Ross Publ, 2013. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-1-60427-070-9.
</dl>`;

export default theory;
